import React from "react";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import { BessResumoDiario } from "../../types";

const NEGATIVE_COLOR = "#f91010";
const POSITIVE_COLOR = "#1fa824";

interface BessMonthChartProps {
  diaria: BessResumoDiario[];
  onSelectDay?: (data: string) => void;
}

interface MonthPointCustom {
  data: string;
  weekend: boolean;
}

interface MonthPoint {
  y: number;
  color?: string;
  custom: MonthPointCustom;
}

/**
 * Returns true when 'YYYY-MM-DD' falls on Saturday or Sunday.
 * The date parts are split from the string to build a local Date, avoiding the
 * UTC shift that `new Date('YYYY-MM-DD')` would introduce.
 */
export function isWeekendDate(data: string): boolean {
  const [year, month, day] = data.split("-").map(Number);
  const weekday = new Date(year, month - 1, day).getDay();
  return weekday === 0 || weekday === 6;
}

export function buildMonthCategories(diaria: BessResumoDiario[]): string[] {
  return diaria.map((d) => {
    const [, month, day] = d.data.split("-");
    return `${day}/${month}`;
  });
}

// A bateria também pode operar em fins de semana (arbitragem por PLD), então
// todo dia é colorido pelo sinal do lucro; o fim de semana é indicado no tooltip.
export function buildMonthPoints(diaria: BessResumoDiario[]): MonthPoint[] {
  return diaria.map((d) => ({
    y: d.lucro,
    color: d.lucro < 0 ? NEGATIVE_COLOR : POSITIVE_COLOR,
    custom: { data: d.data, weekend: isWeekendDate(d.data) },
  }));
}

function BessMonthChart({ diaria, onSelectDay }: BessMonthChartProps) {
  const chartOptions = {
    chart: { type: "column", backgroundColor: "#FAFAFA", borderRadius: "8px" },
    title: { text: "", align: "left" },
    xAxis: {
      categories: buildMonthCategories(diaria),
    },
    yAxis: { title: { text: "Valor em R$" }, gridLineWidth: 0 },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      column: {
        borderRadius: "0%",
      },
      series: {
        point: {
          events: {
            click: function (this: Highcharts.Point): void {
              const custom = this.options.custom as
                | MonthPointCustom
                | undefined;
              if (custom && onSelectDay) onSelectDay(custom.data);
            },
          },
        },
      },
    },
    tooltip: {
      formatter: function (
        this: Highcharts.TooltipFormatterContextObject
      ): string {
        const custom = this.point.options.custom as
          | MonthPointCustom
          | undefined;
        const weekendSuffix = custom?.weekend ? " · fim de semana" : "";
        return `<b>${this.x}${weekendSuffix}<br>${this.y.toLocaleString(
          "pt-br",
          {
            style: "currency",
            currency: "BRL",
          }
        )}</b>`;
      },
    },
    series: {
      label: { enabled: true },
      data: buildMonthPoints(diaria),
      zones: [
        {
          value: 0,
          color: NEGATIVE_COLOR,
        },
        {
          color: POSITIVE_COLOR,
        },
      ],
    },
  };

  return (
    <div style={{ width: "100%" }}>
      <h4 className="bess-section-title">Resultado diário no mês</h4>
      <p style={{ color: "#7e7e82", fontSize: 12, margin: "4px 0 8px" }}>
        clique numa barra para ver o perfil horário do dia
      </p>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </div>
  );
}

export default BessMonthChart;
